import { useRouter } from '@application/utils';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { AiOutlineBell } from 'react-icons/ai';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { EmptyOverlay } from '../EmptyOverlay';
import { LoadingContainer } from '../LoadingContainer';
import * as Styled from './styled';
import type { MtNotificationsPanelProps } from './types';

export * from './types';

// XXX We should try in the future using Popper here
export const MtNotificationsPanel: FC<MtNotificationsPanelProps> = (props) => {
  const { translate, navigateToRoute } = useRouter();
  const {
    isLoading,
    newNotificationsCount,
    isOpen,
    items = [],
    canLoadMore,
    onBellClick,
    onMarkAllAsReadClick,
    onItemClick,
    onClosePanel,
    onLoadMore,
  } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: canLoadMore, // XXX We should try in the future using Popper here

    onLoadMore,
    rootMargin: '0px 0px 10px 0px',
  });

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!bellRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) {
        onClosePanel();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [onClosePanel]);

  const hasUnread = !!newNotificationsCount && newNotificationsCount > 0;

  return (
    <div>
      <div ref={bellRef}>
        <Styled.BellIcon onClick={onBellClick} hasUnread={hasUnread}>
          <AiOutlineBell />
        </Styled.BellIcon>
      </div>

      {isOpen && (
        <Styled.Container ref={panelRef}>
          <Styled.Header>
            <Styled.Title>{translate('mt.notifications.title')}</Styled.Title>
            <Styled.RightAlignedButton
              onClick={onMarkAllAsReadClick}
              disabled={!hasUnread}
              hasUnread={hasUnread}
            >
              {translate('common.buttons.markAllAsRead')}
            </Styled.RightAlignedButton>
          </Styled.Header>

          {!isLoading && items.length === 0 && (
            <EmptyOverlay wrapperProps={{ style: { minHeight: 350 } }} />
          )}

          <Styled.List ref={rootRef}>
            {items.map((notification) => (
              <Styled.Item
                key={notification.id}
                onClick={() => {
                  onItemClick(notification);

                  navigateToRoute(
                    notification.routeOptions.route,
                    notification.routeOptions.options,
                  );
                }}
                read={notification.success.toString()}
              >
                <p>{notification.content}</p>
                {notification.date ? <small>{notification.date}</small> : null}
              </Styled.Item>
            ))}

            {(isLoading || canLoadMore) && (
              <Styled.Item ref={infiniteRef}>
                <LoadingContainer wrapperProps={{ style: { minHeight: 'auto' } }} />
              </Styled.Item>
            )}
          </Styled.List>
        </Styled.Container>
      )}
    </div>
  );
};
